/**
 * Office viewer for the doctor's certificates of a reported absence (#1767).
 *
 * The list carries metadata only. "Open" and "Download" each fetch a fresh
 * 5-minute URL from the audited download route (`inline` is logged as a view,
 * `attachment` as a download); the URL is never cached. Rendered only for
 * admins and `employees` holders, so the list query never runs for a caller
 * the route would answer 403.
 */

import { useLingui } from '@lingui/react';
import { Download, ExternalLink, FileText, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useAbsenceCertificateDownload,
  useAbsenceCertificates,
} from '@/hooks/useEmployeeAbsences';
import { formatDateDisplay } from '@/lib/format';
import type { AbsenceCertificate } from '@/types/employeeAbsences';

export interface AbsenceCertificatesDialogProps {
  open: boolean;
  absenceId: string | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Activate a signed attachment URL without leaving the page: the backend signs
 * it with Content-Disposition: attachment, so the anchor downloads the file.
 * A programmatic anchor click is not subject to the popup blocker (unlike a
 * window.open after an await) and leaves no blank tab behind.
 */
function triggerAttachmentDownload(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function AbsenceCertificatesDialog({
  open,
  absenceId,
  onOpenChange,
}: AbsenceCertificatesDialogProps) {
  const { i18n } = useLingui();
  const { data, isLoading, isError } = useAbsenceCertificates(absenceId ?? '', open);
  const download = useAbsenceCertificateDownload();

  const certificates: AbsenceCertificate[] = data?.certificates ?? [];

  const handleOpen = async (certificate: AbsenceCertificate) => {
    if (!absenceId) return;
    // Open the tab synchronously inside the click: Safari blocks a
    // window.open that runs after an await. A `noopener` feature would make
    // window.open return null, so the opener link is cut by hand instead and
    // the tab is pointed at the signed URL once it arrives.
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      tab.opener = null;
    }
    try {
      const result = await download.mutateAsync({
        absenceId,
        imageId: certificate.id,
        disposition: 'inline',
      });
      if (tab) {
        tab.location.href = result.download_url;
      } else {
        // The popup blocker refused the blank tab; try once more directly.
        window.open(result.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // The hook's onError shows the toast; no blank tab stays behind.
      tab?.close();
    }
  };

  const handleDownload = async (certificate: AbsenceCertificate) => {
    if (!absenceId) return;
    try {
      const result = await download.mutateAsync({
        absenceId,
        imageId: certificate.id,
        disposition: 'attachment',
      });
      triggerAttachmentDownload(result.download_url);
    } catch {
      // The hook's onError shows the toast.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // The title and the list say it all; no separate description.
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'absences.certificates.title', message: 'Certificates' })}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
            <span className="ml-2 text-sm text-muted-foreground">
              {i18n._({ id: 'common.loading.data', message: 'Loading data...' })}
            </span>
          </div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-destructive" role="alert">
            {i18n._({ id: 'common.error.fetch', message: 'Failed to load data' })}
          </p>
        ) : certificates.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {i18n._({
              id: 'absences.certificates.empty',
              message: 'No certificates uploaded.',
            })}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {certificates.map((certificate) => (
              <li
                key={certificate.id}
                className="flex items-center justify-between gap-3 p-3"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium" title={certificate.filename}>
                      {certificate.filename}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {i18n._({
                        id: 'absences.certificates.uploadedAt',
                        message: 'Uploaded {date}',
                        values: { date: formatDateDisplay(certificate.uploaded_at) },
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void handleOpen(certificate)}
                    disabled={download.isPending}
                  >
                    <ExternalLink className="mr-1 h-4 w-4" aria-hidden="true" />
                    {i18n._({ id: 'absences.certificates.open', message: 'Open' })}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void handleDownload(certificate)}
                    disabled={download.isPending}
                  >
                    <Download className="mr-1 h-4 w-4" aria-hidden="true" />
                    {i18n._({ id: 'absences.certificates.download', message: 'Download' })}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
